import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { api } from '../../lib/api.js';

export function AdminOnboarding() {
  const [form, setForm] = useState({
    client_conf_key: '',
    name: '',
    domain: '',
    poc_email: '',
    poc_name: '',
    modules: ['SPIDER'] as string[],
  });

  const submit = useMutation({
    mutationFn: () => api.post('/api/admin/onboarding', form),
  });

  const deeplink = useMutation({
    mutationFn: async () => {
      const res = await api.get<{ url: string; expires_at: number }>('/api/admin/onboarding/deeplink');
      window.open(res.url, '_blank');
    },
  });

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="card">
        <h3 className="text-base font-semibold">Reseller-assisted onboarding</h3>
        <p className="mt-1 text-sm text-slate-500">
          Provisions a new tenant directly via botfeed (no captcha/OTP for authenticated admins).
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3">
          {(
            [
              ['client_conf_key', 'Config key', 'example-app.com'],
              ['name', 'Company name', 'Example Inc'],
              ['domain', 'Primary domain', 'www.example.com'],
              ['poc_email', 'POC email', 'ops@example.com'],
              ['poc_name', 'POC name', 'Jane Doe'],
            ] as const
          ).map(([k, label, ph]) => (
            <label key={k} className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                {label}
              </span>
              <input
                className="input mt-1"
                placeholder={ph}
                value={(form as any)[k]}
                onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
              />
            </label>
          ))}
          <div className="flex gap-2 flex-wrap">
            {['SPIDER', 'MARLIN', 'GECKO', 'RHINO', 'WAHOO'].map((m) => (
              <label key={m} className="text-sm flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={form.modules.includes(m)}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      modules: e.target.checked
                        ? [...f.modules, m]
                        : f.modules.filter((x) => x !== m),
                    }))
                  }
                />
                {m}
              </label>
            ))}
          </div>
          <button
            className="btn-primary"
            disabled={submit.isPending || !form.client_conf_key || !form.name || !form.poc_email}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? 'Submitting…' : 'Onboard'}
          </button>
          {submit.error && (
            <p className="text-sm text-red-600">{(submit.error as Error).message}</p>
          )}
          {submit.isSuccess && submit.data != null ? (
            <pre className="text-xs bg-slate-50 p-2 rounded mt-2 overflow-x-auto">
              {JSON.stringify(submit.data, null, 2)}
            </pre>
          ) : null}
        </div>
      </div>
      <div className="card">
        <h3 className="text-base font-semibold">Public form deeplink</h3>
        <p className="mt-1 text-sm text-slate-500">
          Generate a signed link to <code>register.utility-server.com</code>. Bypasses Turnstile +
          OTP when verified by the CF worker.
        </p>
        <button
          className="btn-secondary mt-3"
          onClick={() => deeplink.mutate()}
          disabled={deeplink.isPending}
        >
          Open signed registration link
        </button>
        {deeplink.error && (
          <p className="text-sm text-red-600 mt-2">{(deeplink.error as Error).message}</p>
        )}
      </div>
    </div>
  );
}
